import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { PositionCard } from '../components/PositionCard';
import { FieldDiagram, PlayerDot } from '../components/FieldDiagram';
import { positionsData } from '../data/nflData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, Swords, Star, Users, ArrowRight, BookOpen } from 'lucide-react';

const Positions = () => {
  // Sample formations for visualization
  const offenseFormation = [
    { x: 50, y: 70, position: "C", color: "#64748b" },
    { x: 40, y: 70, position: "LG", color: "#64748b" },
    { x: 60, y: 70, position: "RG", color: "#64748b" },
    { x: 30, y: 70, position: "LT", color: "#64748b" },
    { x: 70, y: 70, position: "RT", color: "#64748b" },
    { x: 50, y: 55, position: "QB", isQB: true },
    { x: 50, y: 40, position: "RB", color: "#3b82f6" },
    { x: 10, y: 70, position: "WR", color: "#8b5cf6" },
    { x: 90, y: 70, position: "WR", color: "#8b5cf6" },
    { x: 80, y: 70, position: "TE", color: "#06b6d4" }
  ];

  const defenseFormation = [
    { x: 30, y: 70, position: "DE", isDef: true },
    { x: 45, y: 70, position: "DT", isDef: true },
    { x: 55, y: 70, position: "DT", isDef: true },
    { x: 70, y: 70, position: "DE", isDef: true },
    { x: 25, y: 55, position: "OLB", color: "#f97316" },
    { x: 50, y: 55, position: "MLB", color: "#f97316" },
    { x: 75, y: 55, position: "OLB", color: "#f97316" },
    { x: 10, y: 50, position: "CB", color: "#22c55e" },
    { x: 90, y: 50, position: "CB", color: "#22c55e" },
    { x: 35, y: 35, position: "SS", color: "#22c55e" },
    { x: 65, y: 30, position: "FS", color: "#22c55e" }
  ];

  return (
    <div className="min-h-screen" data-testid="positions-page">
      <PageHeader 
        title="Posiciones y Roles"
        subtitle="Conoce qué hace cada jugador en el campo de fútbol americano"
        backgroundImage="https://images.unsplash.com/photo-1547992454-bec8143cc790?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Three Units Overview */}
        <section>
          <SectionHeader 
            title="Las Tres Unidades"
            quarter="INTRO"
            icon={Users}
          />
          
          <p className="text-slate-300 leading-relaxed text-lg mb-8">
            Cada equipo de NFL tiene <strong className="text-white">3 unidades especializadas</strong> que entran 
            al campo en diferentes situaciones. Los jugadores raramente juegan en más de una unidad.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Ofensiva",
                description: "Intenta avanzar el balón y anotar puntos. Entra cuando tu equipo tiene posesión.",
                icon: Swords,
                color: "#3b82f6"
              },
              {
                title: "Defensiva",
                description: "Frena el avance rival e intenta recuperar el balón. Entra cuando el rival tiene posesión.",
                icon: Shield,
                color: "#ef4444"
              },
              {
                title: "Equipos Especiales",
                description: "Jugadas de patada: kickoffs, punts, field goals y returns. Especialistas dedicados.",
                icon: Star,
                color: "#a855f7"
              }
            ].map((unit, index) => (
              <div key={index} className="tactical-card p-6 rounded-r-xl" style={{ borderLeftColor: unit.color }}>
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${unit.color}20` }}
                >
                  <unit.icon className="w-6 h-6" style={{ color: unit.color }} />
                </div>
                <h3 
                  className="text-xl font-bold text-white mb-2 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {unit.title}
                </h3>
                <p className="text-slate-400 text-sm">{unit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Position Tabs */}
        <section>
          <Tabs defaultValue="offense" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-slate-800 rounded-xl p-1 mb-8">
              <TabsTrigger 
                value="offense" 
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Ofensiva
              </TabsTrigger>
              <TabsTrigger 
                value="defense"
                className="data-[state=active]:bg-red-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Defensiva
              </TabsTrigger>
              <TabsTrigger 
                value="special"
                className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-lg font-bold uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Especiales
              </TabsTrigger>
            </TabsList>
            
            {/* Offense Tab */}
            <TabsContent value="offense" className="space-y-8">
              <SectionHeader 
                title="Posiciones Ofensivas"
                icon={Swords}
                subtitle="El equipo que intenta anotar puntos"
              />
              
              {/* Field Visualization */}
              <div className="mb-8">
                <h4 
                  className="text-lg font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Formación ofensiva típica
                </h4>
                <div className="glow-green rounded-xl overflow-hidden">
                  <FieldDiagram showEndZones={false}>
                    {offenseFormation.map((player, index) => (
                      <PlayerDot 
                        key={index}
                        x={player.x}
                        y={player.y}
                        position={player.position}
                        isQB={player.isQB}
                        color={player.color}
                      />
                    ))}
                  </FieldDiagram>
                </div>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-yellow-500" /> QB
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-blue-500" /> RB
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-purple-500" /> WR
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-cyan-500" /> TE
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-slate-500" /> OL
                  </span>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {positionsData.offense.map((position, index) => (
                  <PositionCard key={index} {...position} />
                ))}
              </div>
            </TabsContent>
            
            {/* Defense Tab */}
            <TabsContent value="defense" className="space-y-8">
              <SectionHeader 
                title="Posiciones Defensivas"
                icon={Shield}
                subtitle="El equipo que intenta frenar al rival"
              />
              
              {/* Field Visualization */}
              <div className="mb-8">
                <h4 
                  className="text-lg font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Formación defensiva 4-3
                </h4>
                <div className="rounded-xl overflow-hidden" style={{ boxShadow: '0 0 40px -10px rgba(239, 68, 68, 0.3)' }}>
                  <FieldDiagram showEndZones={false}>
                    {defenseFormation.map((player, index) => (
                      <PlayerDot 
                        key={index}
                        x={player.x}
                        y={player.y}
                        position={player.position}
                        isDef={player.isDef}
                        color={player.color}
                      />
                    ))}
                  </FieldDiagram>
                </div>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-red-500" /> DL
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-orange-500" /> LB
                  </span>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-emerald-500" /> DB
                  </span>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {positionsData.defense.map((position, index) => (
                  <PositionCard key={index} {...position} />
                ))}
              </div>
            </TabsContent>
            
            {/* Special Teams Tab */}
            <TabsContent value="special" className="space-y-8">
              <SectionHeader 
                title="Equipos Especiales"
                icon={Star}
                subtitle="Los especialistas de las jugadas de patada"
              />
              
              <div className="tactical-card p-6 rounded-r-xl mb-8" style={{ borderLeftColor: '#a855f7' }}>
                <p className="text-slate-300 leading-relaxed">
                  Los equipos especiales entran en <strong className="text-white">jugadas de patada</strong>: 
                  kickoffs al inicio de cada mitad y después de anotaciones, punts cuando renuncias a la posesión, 
                  y field goals/puntos extra cuando intentas patear entre los postes. Aunque juegan pocas jugadas, 
                  pueden ser <strong className="text-purple-400">decisivos</strong> en partidos cerrados.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {positionsData.specialTeams.map((position, index) => (
                  <PositionCard key={index} {...position} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
};

export default Positions;
